import { useEffect, useRef, useState } from 'react';
import { data } from '../bridge/client';
import type { StoreTable } from '../data/db/database';
import { count } from './perf';
import { share } from '../core/structuralShare';
import type { RebuildPacer } from './rebuildPacer';

/**
 * Store-observing state: runs `read` now, and again whenever one of `tables` changes — the web
 * twin of a view model observing StoreChangeNotifier. Re-reads are coalesced to one per frame
 * (the "≤1 reload per ~150 ms, last write wins" rule — a plain throttle would drop the final
 * one), and an answer that arrives after a newer request was made is discarded, so a slow read
 * can never paint over a newer one. Answers are structurally shared with the previous one
 * (core/structuralShare.ts): an unchanged re-read does not re-render at all.
 */
export function useLive<T>(
  read: () => Promise<T>,
  tables: StoreTable[],
  deps: unknown[],
  // `pacer` (P111): re-reads wait out the last rebuild's own cost (ui/rebuildPacer.ts) and none runs
  // while the tab is hidden — the list is marked stale and rebuilt once on return.
  options: { initial?: () => T | undefined; first?: () => Promise<T> | null; pacer?: RebuildPacer } = {},
): { value: T | undefined; loadedFor: string } {
  const [state, setState] = useState<{ value: T | undefined; loadedFor: string }>(() => {
    const initial = options.initial?.();
    return { value: initial, loadedFor: initial === undefined ? '' : 'cache' };
  });
  const generation = useRef(0);
  const key = JSON.stringify(deps);
  useEffect(() => {
    let disposed = false;
    let scheduled = false;
    let firstRun = true;
    let stale = false;
    const pacer = options.pacer;
    const run = () => {
      scheduled = false;
      if (pacer && !firstRun && document.visibilityState === 'hidden') {
        stale = true;
        return;
      }
      const mine = ++generation.current;
      count('live.read');
      const startedAt = performance.now();
      pacer?.begin();
      const pending = (firstRun && options.first?.()) || read();
      firstRun = false;
      pending.then(
        (value) => {
          if (pacer) {
            const now = performance.now();
            pacer.add(now - startedAt, now);
          }
          if (disposed || mine !== generation.current) return;
          // Structural sharing: a re-read that found nothing new keeps the SAME object, so nothing
          // re-renders; a real change keeps every untouched subtree (memoized rows stay put).
          setState((prev) => {
            const shared = share(prev.value, value);
            return shared === prev.value && prev.loadedFor === key ? prev : { value: shared, loadedFor: key };
          });
        },
        () => undefined,
      );
    };
    run();
    const watched = new Set(tables);
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      const wait = pacer ? pacer.waitMs(performance.now()) : 0;
      if (wait > 0) setTimeout(() => !disposed && requestAnimationFrame(run), wait);
      else requestAnimationFrame(run);
    };
    const off = data.onChanges((changed) => {
      for (const t of changed) {
        if (watched.has(t)) {
          schedule();
          return;
        }
      }
    });
    const onVisible = () => {
      if (stale && document.visibilityState === 'visible') {
        stale = false;
        schedule();
      }
    };
    if (pacer) document.addEventListener('visibilitychange', onVisible);
    return () => {
      disposed = true;
      off();
      if (pacer) document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state;
}

/** A clock that ticks once a minute — relative times ("hace 5 min", "ayer") stay honest. */
export function useMinuteClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export function useLocalStorage<T extends string>(key: string, initial: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      return (localStorage.getItem(key) as T | null) ?? initial;
    } catch {
      return initial;
    }
  });
  const set = (v: T) => {
    setValue(v);
    try {
      localStorage.setItem(key, v);
    } catch {
      /* a per-viewer convenience; losing it costs nothing */
    }
  };
  return [value, set];
}
